import * as React from "react";
import Icon from "../../Icon";
import { motion } from "framer-motion";
import AddToCalendar from "@culturehq/add-to-calendar";
import "@culturehq/add-to-calendar/dist/styles.css";
import DialogModal from "../../Dialog";
import LiveSchedules from "../../LiveSchedules";
import { GoogleMap, useLoadScript, MarkerF } from "@react-google-maps/api";
import Button from "@src/components/Button";
import formatDateWithOrdinal, {
  formatEventTimeWithTimeZoneLabel,
} from "@src/utils/dateFormatter";
import { IParts } from "@src/components/Parts";
import { useEventParts } from "@src/hooks/useEventParts";

const Parts: React.FC<IParts> = ({
  eventId,
  parts,
  eventPartsData,
  liveSchedules,
  eventTimezone,
  isInvitedToEvent,
}) => {
  const [openMap, setOpenMap] = React.useState<boolean>(false);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || "",
    libraries: ["places"],
  });

  const onLoadMarker = (marker: any) => {
    // console.log("Marker", marker?.position?.lat);
  };

  const handleViewDirections = ({
    latitude,
    longitude,
  }: {
    latitude: number;
    longitude: number;
  }) => {
    const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}&travelmode=driving`;
    window.open(directionsUrl, "_blank");
  };

  const handleFindHotelsNearby = ({
    latitude,
    longitude,
  }: {
    latitude: number;
    longitude: number;
  }) => {
    const hotelsUrl = `https://www.google.com/maps/search/hotels/@${latitude},${longitude},15z`;
    window.open(hotelsUrl, "_blank");
  };

  const { parts: derivedParts } = useEventParts({
    eventPartsData,
    liveSchedules,
    eventTimezone,
    isInvitedToEvent,
  });
  const visibleParts = parts ?? derivedParts;

  const addOneDay = (dateTime: string) => {
    var date = new Date(dateTime);
    date.setHours(date.getHours() + 24);
    var isoDate = date.toISOString();
    return isoDate;
  };

  return visibleParts.length > 0 ? (
    <div className={``}>
      {visibleParts.length === 0 ? (
        <></>
      ) : (
        <motion.div
          initial={{ opacity: 1 }}
          whileInView={{
            opacity: 1,
            transition: {
              delayChildren: 0.5,
              staggerChildren: 0.5,
            },
          }}
          className={`bg-[#876935] py-[5rem] lg:py-[10rem]`}
        >
          <h3 className="font-ntFab font-semibold lg:font-normal text-2xl lg:text-6xl text-center text-[#FFFFFF] mb-20">
            The Event Schedule
          </h3>
          <div className="flex flex-col items-center justify-center w-full">
            {visibleParts.map((item, idx) => (
              <motion.div
                initial={{ opacity: 0, y: -15 }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                  transition: {
                    delay: 0.2,
                    duration: 1,
                    ease: [0.79, 0.14, 0.15, 0.86],
                  },
                }}
                key={`home action ${idx}`}
                className={`flex flex-col lg:flex-row justify-center items-center gap-y-4 gap-x-8 w-full max-w-6xl mx-auto border-b ${
                  idx === 0 ? `border-t` : ``
                } border-y-white py-8`}
              >
                <h1 className="flex-1 min-w-0 px-2 text-center font-eavesSmallCaps text-white font-medium lg:text-2xl whitespace-normal break-words">
                  {item.name}
                </h1>
                <p className="flex-1 min-w-0 text-center text-white font-ebGaramond truncate">
                  {formatDateWithOrdinal(
                    item.time,
                    item.eventPartTimezone ?? eventTimezone
                  )}
                </p>
                <p className="flex-1 min-w-0 text-center font-ebGaramond text-white">
                  {formatEventTimeWithTimeZoneLabel(
                    item.time,
                    item.eventPartTimezone ?? eventTimezone
                  )}
                </p>
                {item.showAddress ? (
                  <p className="flex-1 min-w-0 text-center font-ebGaramond text-[#ffffff] max-w-sm sm:max-w-none md:whitespace-normal">
                    {item?.address}
                  </p>
                ) : (
                  <></>
                )}
                <div className="flex-1 min-w-0 flex flex-col items-center gap-2">
                  {item.showAddress && (
                    <div className="w-[150px] flex justify-center border border-[#FFFFFF] rounded">
                      <AddToCalendar
                        event={{
                          name: item.name,
                          details: null,
                          location: item?.address,
                          startsAt: item?.time,
                          endsAt: addOneDay(item?.time),
                        }}
                      >
                        <span className="flex items-center justify-center text-[#FFFFFF] font-inter text-base transition-colors duration-200 h-[1.9rem]">
                          Save Date
                        </span>
                        {/* <div className="w-full flex justify-center">
                      </div> */}
                      </AddToCalendar>
                    </div>
                  )}

                  {item.showAddress && item.hasLiveStream && liveSchedules && (
                    <DialogModal
                      fillScreen={true}
                      trigger={
                        <div className="w-[150px] flex justify-center border border-[#FFFFFF] rounded py-2 group">
                          <button
                            type="button"
                            className="w-full flex items-center justify-center font-inter text-base !text-[#FFFFFF] bg-transparent py-2 transition-all duration-200 rounded"
                            style={{ fontWeight: 500 }}
                          >
                            <span className="transition-all duration-200 group-hover:text-[1.05rem] hover:text-[1.05rem] !text-[#FFFFFF]">
                              Watch Live
                            </span>
                          </button>
                        </div>
                      }
                    >
                      <LiveSchedules schedules={liveSchedules} />
                    </DialogModal>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  ) : (
    <></>
  );
};

export default Parts;
